(() => {
  'use strict';

  const TARGET_PRODUCT = '/products/ariston-tote';
  const OFFER_HEADING = 'you might also like these';
  const TOGGLE_LABEL = 'Add-ons';
  const TOGGLE_CLASS = 'neyuh-selleasy-toggle';
  const COLLAPSED_CLASS = 'neyuh-selleasy-offer--collapsed';
  const STANDARD_DELIVERY = /(^|[^\d])7\s*[-–—]\s*10\s*days?\b/i;
  const NATIVE_CLASS = 'neyuh-selleasy-native-variant-select';
  const CUSTOM_CLASS = 'neyuh-selleasy-options';
  const productCache = new Map();
  const currencySyncRoots = new Set();
  let catalogPromise;
  let scanQueued = false;
  let currencySyncQueued = false;

  if (window.location.pathname.replace(/\/$/, '') !== TARGET_PRODUCT) return;

  const normalize = (value) => String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();

  const isStandardDelivery = (value) => STANDARD_DELIVERY.test(String(value || ''));

  function variantIdFromOption(option) {
    if (!option) return null;
    const candidates = [
      option.value,
      option.dataset.variantId,
      option.dataset.variant,
      option.getAttribute('data-id'),
    ];
    return candidates.map(String).find((value) => /^\d{8,}$/.test(value)) || null;
  }

  function looksLikeVariantSelect(select) {
    if (select.classList.contains('neyuh-selleasy-option')) return false;
    if (select.matches('.lb-variant-picker')) return true;
    return Array.from(select.options).some((option) => {
      return variantIdFromOption(option) || /\s\/\s/.test(option.textContent || '');
    });
  }

  function findOfferRoot() {
    const headings = Array.from(document.querySelectorAll('main *')).filter((element) => {
      return normalize(element.textContent) === OFFER_HEADING;
    });

    for (const heading of headings) {
      let node = heading.parentElement;
      while (node && node.matches('main, main *')) {
        const selects = Array.from(node.querySelectorAll('select')).filter(looksLikeVariantSelect);
        if (selects.length && node.querySelector('input[type="checkbox"]')) return node;
        node = node.parentElement;
      }
    }
    return null;
  }

  function initializeOfferToggle(root) {
    if (root.dataset.neyuhSelleasyToggleInitialized === 'true') return;

    const layoutBoundary = root.closest('.group-block-content') || root.closest('.product-details');
    if (layoutBoundary) {
      let layoutWrapper = root.parentElement;
      while (layoutWrapper && layoutWrapper !== layoutBoundary) {
        layoutWrapper.classList.add('neyuh-selleasy-layout-wrapper');
        layoutWrapper = layoutWrapper.parentElement;
      }
    }

    const originalHeadings = Array.from(root.querySelectorAll('*')).filter((element) => {
      return normalize(element.textContent) === OFFER_HEADING;
    });
    originalHeadings.forEach((heading) => heading.classList.add('neyuh-selleasy-original-heading'));

    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = TOGGLE_CLASS;
    toggle.setAttribute('aria-expanded', 'false');

    const label = document.createElement('span');
    label.className = `${TOGGLE_CLASS}__label`;
    label.textContent = TOGGLE_LABEL;

    const icon = document.createElement('span');
    icon.className = `${TOGGLE_CLASS}__icon`;
    icon.setAttribute('aria-hidden', 'true');

    toggle.append(label, icon);
    root.prepend(toggle);
    root.classList.add('neyuh-selleasy-offer', COLLAPSED_CLASS);
    root.dataset.neyuhSelleasyToggleInitialized = 'true';

    toggle.addEventListener('click', () => {
      const collapsed = root.classList.toggle(COLLAPSED_CLASS);
      toggle.setAttribute('aria-expanded', String(!collapsed));
    });
  }

  function findCard(nativeSelect, root) {
    const selleasyCard = nativeSelect.closest('lb-upsell-flat-card') || nativeSelect.closest('.lb-flat-card');
    if (selleasyCard && root.contains(selleasyCard)) return selleasyCard;

    let node = nativeSelect.parentElement;
    while (node && node !== root) {
      const nativeSelects = Array.from(node.querySelectorAll('select')).filter(looksLikeVariantSelect);
      if (nativeSelects.length === 1 && node.querySelector('input[type="checkbox"]')) return node;
      node = node.parentElement;
    }
    return null;
  }

  function handleFromCard(card) {
    const selleasyHandle = card.getAttribute('lb-product-handle');
    if (selleasyHandle) return selleasyHandle;

    const productLink = Array.from(card.querySelectorAll('a[href*="/products/"]')).find((link) => {
      return !link.href.includes(TARGET_PRODUCT);
    });
    if (!productLink) return null;

    try {
      const match = new URL(productLink.href, window.location.origin).pathname.match(/^\/products\/([^/]+)/);
      return match ? decodeURIComponent(match[1]) : null;
    } catch (_error) {
      return null;
    }
  }

  async function fetchJson(url) {
    const response = await fetch(url, { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`Request failed (${response.status}): ${url}`);
    return response.json();
  }

  async function getCatalog() {
    catalogPromise ||= fetchJson('/products.json?limit=250').then((data) => data.products || []);
    return catalogPromise;
  }

  function normalizeProduct(product) {
    const optionCount = product.options?.length || 0;
    return {
      ...product,
      variants: (product.variants || []).map((variant) => ({
        ...variant,
        options: Array.isArray(variant.options)
          ? variant.options
          : [variant.option1, variant.option2, variant.option3].slice(0, optionCount),
      })),
    };
  }

  async function getProduct(card, nativeSelect) {
    const handle = handleFromCard(card);
    if (handle) {
      if (!productCache.has(handle)) {
        productCache.set(handle, fetchJson(`/products/${encodeURIComponent(handle)}.js`));
      }
      return productCache.get(handle).then(normalizeProduct);
    }

    const selectedId = variantIdFromOption(nativeSelect.selectedOptions[0]);
    if (!selectedId) throw new Error('Selleasy variant ID and product link were not found.');

    const products = await getCatalog();
    const product = products.find((item) => {
      return item.variants.some((variant) => String(variant.id) === selectedId);
    });
    if (!product) throw new Error(`Product for Selleasy variant ${selectedId} was not found.`);
    return normalizeProduct(product);
  }

  function nativeOptionForVariant(nativeSelect, variant) {
    return Array.from(nativeSelect.options).find((option) => {
      const optionVariantId = variantIdFromOption(option);
      if (optionVariantId) return optionVariantId === String(variant.id);
      return normalize(option.textContent) === normalize(variant.title);
    });
  }

  function variantForNativeSelection(nativeSelect, variants) {
    const selected = nativeSelect.selectedOptions[0];
    if (!selected) return null;
    const selectedId = variantIdFromOption(selected);
    if (selectedId) return variants.find((variant) => String(variant.id) === selectedId) || null;
    return variants.find((variant) => normalize(variant.title) === normalize(selected.textContent)) || null;
  }

  function optionLabel(option, index) {
    const name = typeof option === 'string' ? option : option.name;
    if (/pre[\s-]?order|shipping|delivery|fulfil|fulfill/i.test(name || '')) return 'Delivery';
    return name || `Option ${index + 1}`;
  }

  function uniqueValues(variants, optionIndex) {
    return variants.reduce((values, variant) => {
      const value = variant.options[optionIndex];
      if (value != null && !values.includes(value)) values.push(value);
      return values;
    }, []);
  }

  function standardDeliveryOptionIndex(product, variants) {
    return (product.options || []).findIndex((_option, optionIndex) => {
      return variants.some((variant) => isStandardDelivery(variant.options[optionIndex]));
    });
  }

  function buildCustomOptions(product, variants, hiddenOptionIndexes) {
    const wrapper = document.createElement('div');
    wrapper.className = CUSTOM_CLASS;
    wrapper.setAttribute('aria-label', `${product.title} options`);

    const optionDefinitions = product.options || [];
    optionDefinitions.forEach((option, optionIndex) => {
      if (hiddenOptionIndexes.has(optionIndex)) return;
      if (!uniqueValues(variants, optionIndex).length) return;

      const field = document.createElement('label');
      field.className = 'neyuh-selleasy-field';

      const label = document.createElement('span');
      label.className = 'neyuh-selleasy-label';
      label.textContent = optionLabel(option, optionIndex);

      const select = document.createElement('select');
      select.className = 'neyuh-selleasy-option';
      select.dataset.optionIndex = String(optionIndex);
      select.setAttribute('aria-label', `${product.title}: ${label.textContent}`);

      uniqueValues(variants, optionIndex).forEach((value) => {
        const item = document.createElement('option');
        item.value = value;
        item.textContent = value;
        select.append(item);
      });

      field.append(label, select);
      wrapper.append(field);
    });
    if (wrapper.childElementCount === 1) wrapper.classList.add(`${CUSTOM_CLASS}--single`);
    return wrapper;
  }

  function selectedCustomOptions(wrapper) {
    return Array.from(wrapper.querySelectorAll('.neyuh-selleasy-option')).reduce((selected, select) => {
      selected[Number(select.dataset.optionIndex)] = select.value;
      return selected;
    }, []);
  }

  function updateAvailableValues(wrapper, variants) {
    const selects = Array.from(wrapper.querySelectorAll('.neyuh-selleasy-option'));
    const selected = selectedCustomOptions(wrapper);

    selects.forEach((select, selectPosition) => {
      const optionIndex = Number(select.dataset.optionIndex);
      Array.from(select.options).forEach((option) => {
        const possible = variants.some((variant) => {
          if (!variant.available || variant.options[optionIndex] !== option.value) return false;
          return selects.slice(0, selectPosition).every((previousSelect) => {
            const previousIndex = Number(previousSelect.dataset.optionIndex);
            return variant.options[previousIndex] === selected[previousIndex];
          });
        });
        option.disabled = !possible;
      });
    });
  }

  function syncCustomFromVariant(wrapper, variant) {
    if (!variant) return;

    wrapper.querySelectorAll('.neyuh-selleasy-option').forEach((select) => {
      const value = variant.options[Number(select.dataset.optionIndex)];
      if (value != null) select.value = value;
    });
  }

  function syncCustomFromNative(nativeSelect, wrapper, variants, allVariants) {
    const nativeVariant = variantForNativeSelection(nativeSelect, allVariants);
    if (!nativeVariant) return;

    const visibleOptionIndexes = Array.from(wrapper.querySelectorAll('.neyuh-selleasy-option')).map((select) => {
      return Number(select.dataset.optionIndex);
    });
    const variant = variants.find((item) => {
      return item.available && visibleOptionIndexes.every((index) => item.options[index] === nativeVariant.options[index]);
    }) || variants.find((item) => item.available);
    if (!variant) return;

    syncCustomFromVariant(wrapper, variant);
    updateAvailableValues(wrapper, variants);

    if (String(variant.id) === String(nativeVariant.id)) return;
    const nativeOption = nativeOptionForVariant(nativeSelect, variant);
    if (!nativeOption || nativeOption.disabled) return;

    nativeSelect.selectedIndex = nativeOption.index;
    nativeSelect.dispatchEvent(new Event('input', { bubbles: true }));
    nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function queueCurrencySync(root) {
    currencySyncRoots.add(root);
    if (currencySyncQueued) return;
    currencySyncQueued = true;

    queueMicrotask(() => {
      currencySyncQueued = false;
      let needsConversion = false;

      currencySyncRoots.forEach((scope) => {
        scope.querySelectorAll('[lb-price].buckscc-converted').forEach((element) => {
          const selleasyPrice = element.getAttribute('lb-price');
          const convertedSource = element.getAttribute('bucks-original');
          if (!selleasyPrice || selleasyPrice === convertedSource) return;

          element.classList.remove('buckscc-converted', 'buckscc-money');
          ['bucks-original', 'bucks-init', 'bucks-currency', 'bucks-current'].forEach((attribute) => {
            element.removeAttribute(attribute);
          });
          element.textContent = selleasyPrice;
          needsConversion = true;
        });
      });
      currencySyncRoots.clear();

      if (needsConversion && typeof window.bucksCC?.reConvert === 'function') {
        window.bucksCC.reConvert();
      }
    });
  }

  function syncNativeFromCustom(nativeSelect, wrapper, variants, changedSelect) {
    const selected = selectedCustomOptions(wrapper);
    let variant = variants.find((item) => {
      return item.available && selected.every((value, index) => value == null || item.options[index] === value);
    });

    if (!variant && changedSelect) {
      const changedIndex = Number(changedSelect.dataset.optionIndex);
      variant = variants.find((item) => {
        return item.available && item.options[changedIndex] === selected[changedIndex];
      });
      if (variant) {
        syncCustomFromVariant(wrapper, variant);
      }
    }

    if (!variant) {
      syncCustomFromNative(nativeSelect, wrapper, variants, variants);
      return;
    }

    const nativeOption = nativeOptionForVariant(nativeSelect, variant);
    if (!nativeOption || nativeOption.disabled) {
      syncCustomFromNative(nativeSelect, wrapper, variants, variants);
      return;
    }

    nativeSelect.selectedIndex = nativeOption.index;
    nativeSelect.dispatchEvent(new Event('input', { bubbles: true }));
    nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
    updateAvailableValues(wrapper, variants);
  }

  async function initializeSelect(nativeSelect, root) {
    if (nativeSelect.dataset.neyuhSelleasyInitialized === 'pending' ||
        nativeSelect.dataset.neyuhSelleasyInitialized === 'true') return;

    const card = findCard(nativeSelect, root);
    if (!card) return;
    nativeSelect.dataset.neyuhSelleasyInitialized = 'pending';

    try {
      const product = await getProduct(card, nativeSelect);
      const allVariants = product.variants.filter((variant) => nativeOptionForVariant(nativeSelect, variant));
      const deliveryOptionIndex = standardDeliveryOptionIndex(product, allVariants);
      const hiddenOptionIndexes = new Set();
      let variants = allVariants;

      if (deliveryOptionIndex >= 0) {
        hiddenOptionIndexes.add(deliveryOptionIndex);
        variants = allVariants.filter((variant) => isStandardDelivery(variant.options[deliveryOptionIndex]));
      }

      if (!variants.length || !product.options || product.options.length < 2) {
        delete nativeSelect.dataset.neyuhSelleasyInitialized;
        return;
      }

      card.querySelectorAll(`.${CUSTOM_CLASS}`).forEach((element) => element.remove());
      const wrapper = buildCustomOptions(product, variants, hiddenOptionIndexes);
      if (!wrapper.childElementCount) {
        delete nativeSelect.dataset.neyuhSelleasyInitialized;
        return;
      }
      nativeSelect.insertAdjacentElement('beforebegin', wrapper);

      root.classList.add('neyuh-selleasy-offer');
      card.classList.add('neyuh-selleasy-card');
      card.dataset.customAddonInitialized = 'true';
      nativeSelect.classList.add(NATIVE_CLASS);
      nativeSelect.dataset.neyuhSelleasyInitialized = 'true';

      syncCustomFromNative(nativeSelect, wrapper, variants, allVariants);
      wrapper.addEventListener('change', (event) => {
        syncNativeFromCustom(nativeSelect, wrapper, variants, event.target);
      });
      nativeSelect.addEventListener('input', () => syncCustomFromNative(nativeSelect, wrapper, variants, allVariants));
      nativeSelect.addEventListener('change', () => syncCustomFromNative(nativeSelect, wrapper, variants, allVariants));

      const nativeObserver = new MutationObserver(() => {
        syncCustomFromNative(nativeSelect, wrapper, variants, allVariants);
      });
      nativeObserver.observe(nativeSelect, {
        attributes: true,
        attributeFilter: ['value', 'disabled'],
        childList: true,
        subtree: true,
      });

      const priceObserver = new MutationObserver(() => queueCurrencySync(card));
      priceObserver.observe(card, {
        attributes: true,
        attributeFilter: ['lb-price'],
        childList: true,
        subtree: true,
      });
    } catch (error) {
      delete nativeSelect.dataset.neyuhSelleasyInitialized;
      console.warn('[Neyuh Selleasy add-ons]', error);
    }
  }

  function scan() {
    scanQueued = false;
    const root = findOfferRoot();
    if (!root) return;
    initializeOfferToggle(root);
    Array.from(root.querySelectorAll('select')).filter(looksLikeVariantSelect).forEach((select) => {
      initializeSelect(select, root);
    });
  }

  function queueScan() {
    if (scanQueued) return;
    scanQueued = true;
    queueMicrotask(scan);
  }

  const observer = new MutationObserver(queueScan);
  observer.observe(document.documentElement, { childList: true, subtree: true });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', queueScan, { once: true });
  } else {
    queueScan();
  }
})();
